/**
 * Product/operations building blocks shared by the App and the Admin console (owner review 2026-10-05):
 * grouped action menu, pagination, KPI tiles, key–value grids, URL-controllable page tabs, filter
 * selects and active-filter chips, and the display-only `Amount`. None of these compute money.
 */
import { ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  Button as AriaButton,
  Header,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Popover,
  Tab,
  TabList,
  TabPanel,
  Tabs as AriaTabs,
  Text,
  type Key,
} from 'react-aria-components';
import { cx } from '../cx.ts';
import { formatAmount } from '../format.ts';

/* ───────── Grouped action menu («عملیات مدیریت ▾») ───────── */

export type ActionTone = 'neutral' | 'positive' | 'caution' | 'danger';

export interface ActionMenuItem {
  id: string;
  label: string;
  description?: string;
  tone?: ActionTone;
  isDisabled?: boolean;
}
export interface ActionMenuSection {
  title: string;
  items: ActionMenuItem[];
}

const TONE_DOT: Record<ActionTone, string> = {
  neutral: 'bg-fg-subtle',
  positive: 'bg-positive',
  caution: 'bg-accent',
  danger: 'bg-negative',
};

export function ActionMenu({
  label,
  sections,
  onAction,
  variant = 'primary',
  size = 'md',
  ...rest
}: {
  label: string;
  sections: ActionMenuSection[];
  onAction: (id: string) => void;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md';
} & Record<`data-${string}`, unknown>) {
  return (
    <MenuTrigger>
      <AriaButton
        {...rest}
        className={cx(
          'inline-flex items-center gap-2 whitespace-nowrap rounded-md font-semibold outline-hidden cursor-pointer',
          'data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus data-[focus-visible]:outline-offset-2',
          size === 'sm' ? 'h-9 px-3.5 text-[13px]' : 'h-11 px-4 text-sm',
          variant === 'primary'
            ? 'bg-primary text-on-primary hover:brightness-110'
            : 'border border-line-strong bg-surface-2 text-fg hover:bg-surface-3',
        )}
      >
        {label}
        <ChevronDown aria-hidden className="size-4" />
      </AriaButton>
      <Popover
        placement="bottom end"
        offset={6}
        className="w-[min(92vw,340px)] overflow-auto rounded-lg border border-line-strong bg-surface-2 p-1.5 shadow-float outline-hidden data-[entering]:animate-[hx-pop-in_var(--hx-duration-fast)_var(--hx-ease-enter)]"
      >
        <Menu
          aria-label={label}
          onAction={(k: Key) => onAction(String(k))}
          className="max-h-[70dvh] outline-hidden"
        >
          {sections.map((s) => (
            <MenuSection key={s.title} className="py-1 [&+&]:border-t [&+&]:border-line">
              <Header className="px-3 pb-1 pt-1.5 text-[11px] font-semibold text-fg-subtle">
                {s.title}
              </Header>
              {s.items.map((i) => (
                <MenuItem
                  key={i.id}
                  id={i.id}
                  textValue={i.label}
                  isDisabled={i.isDisabled}
                  data-menu-action={i.id}
                  className={cx(
                    'flex cursor-pointer items-start gap-2.5 rounded-sm px-3 py-2 text-[13px] outline-hidden',
                    'data-[focused]:bg-surface-3 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-45',
                    i.tone === 'danger' ? 'text-negative' : 'text-fg',
                  )}
                >
                  <span
                    aria-hidden
                    className={cx(
                      'mt-2 size-1.5 shrink-0 rounded-full',
                      TONE_DOT[i.tone ?? 'neutral'],
                    )}
                  />
                  <span className="flex min-w-0 flex-col">
                    <Text slot="label" className="font-medium">
                      {i.label}
                    </Text>
                    {i.description && (
                      <Text slot="description" className="text-[11.5px] leading-5 text-fg-muted">
                        {i.description}
                      </Text>
                    )}
                  </span>
                </MenuItem>
              ))}
            </MenuSection>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}

/* ───────── Pagination ───────── */

const fa = (n: number) => n.toLocaleString('fa-IR');

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
  label = 'صفحه‌بندی',
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
  label?: string;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const btn =
    'inline-grid size-9 place-items-center rounded-sm border border-line bg-surface-1 text-fg outline-hidden cursor-pointer hover:bg-surface-2 data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus';
  return (
    <nav aria-label={label} className="flex items-center justify-between gap-3 pt-3 text-[12.5px]">
      <p className="m-0 text-fg-muted" aria-live="polite">
        نمایش {fa(from)}–{fa(to)} از {fa(total)}
      </p>
      <div className="flex items-center gap-2">
        {/* RTL: «قبلی» points right, «بعدی» points left. */}
        <AriaButton
          aria-label="صفحه‌ی قبل"
          isDisabled={page <= 1}
          onPress={() => onPage(page - 1)}
          className={btn}
        >
          <ChevronRight aria-hidden className="size-4" />
        </AriaButton>
        <span className="min-w-16 text-center text-fg">
          صفحه‌ی {fa(page)} از {fa(pages)}
        </span>
        <AriaButton
          aria-label="صفحه‌ی بعد"
          isDisabled={page >= pages}
          onPress={() => onPage(page + 1)}
          className={btn}
        >
          <ChevronLeft aria-hidden className="size-4" />
        </AriaButton>
      </div>
    </nav>
  );
}

/* ───────── KPI tile and key–value grid ───────── */

/** A metric. `value === null` renders «—» with a reason — never a fabricated zero. */
export function KpiTile({
  label,
  value,
  hint,
  tone = 'neutral',
  unavailableReason,
  size = 'md',
  className,
  ...rest
}: {
  label: string;
  value: ReactNode | null;
  hint?: ReactNode;
  tone?: 'neutral' | 'positive' | 'negative' | 'caution';
  unavailableReason?: string;
  /** `sm`: dense metric grids (Customer 360 financial overview). */
  size?: 'sm' | 'md';
  className?: string;
} & Record<`data-${string}`, unknown>) {
  const unavailable = value === null || value === undefined;
  return (
    <div
      {...rest}
      className={cx(
        'flex min-w-0 flex-col gap-1 rounded-md border border-line bg-surface-1',
        size === 'sm' ? 'px-3 py-2.5' : 'p-4',
        className,
      )}
    >
      <span className={cx('text-fg-muted', size === 'sm' ? 'text-[11.5px]' : 'text-[12px]')}>
        {label}
      </span>
      <span
        className={cx(
          // Long amounts wrap inside the tile (unit drops below the number); never overflow it.
          'hx-num min-w-0 max-w-full font-extrabold leading-tight [overflow-wrap:anywhere]',
          size === 'sm' ? 'text-[16px]' : 'text-[22px]',
          unavailable
            ? 'text-fg-subtle'
            : tone === 'positive'
              ? 'text-positive'
              : tone === 'negative'
                ? 'text-negative'
                : tone === 'caution'
                  ? 'text-accent-text'
                  : 'text-fg',
        )}
        {...(unavailable ? { 'aria-label': `${label}: نامشخص`, 'data-unavailable': '' } : {})}
      >
        {unavailable ? '—' : value}
      </span>
      {(hint || (unavailable && unavailableReason)) && (
        <span className="text-[11.5px] leading-5 text-fg-subtle">
          {unavailable ? unavailableReason : hint}
        </span>
      )}
    </div>
  );
}

export function KeyValueGrid({
  items,
  columns = 2,
  className,
}: {
  items: { label: string; value: ReactNode; wide?: boolean }[];
  columns?: 1 | 2 | 3 | 4;
  className?: string;
}) {
  const cols = {
    1: '',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 xl:grid-cols-3',
    4: 'sm:grid-cols-2 xl:grid-cols-4',
  }[columns];
  return (
    <dl className={cx('m-0 grid gap-x-6 gap-y-3', cols, className)}>
      {items.map((i) => (
        <div key={i.label} className={cx('min-w-0', i.wide && 'sm:col-span-full')}>
          <dt className="text-[12px] text-fg-muted">{i.label}</dt>
          <dd className="m-0 mt-0.5 text-[13.5px] font-semibold text-fg">{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ───────── Page tabs (controlled, URL-friendly) ───────── */

export interface PageTab {
  id: string;
  label: string;
  content: ReactNode;
  badge?: ReactNode;
}

export function PageTabs({
  label,
  items,
  selected,
  onSelect,
  sticky,
}: {
  label: string;
  items: PageTab[];
  selected: string;
  onSelect: (id: string) => void;
  /** Top offset for a sticky tab bar (e.g. below a sticky header). */
  sticky?: string;
}) {
  return (
    <AriaTabs selectedKey={selected} onSelectionChange={(k: Key) => onSelect(String(k))}>
      <div
        className={cx(
          'z-20 -mx-1 overflow-x-auto border-b border-line bg-canvas px-1 [scrollbar-width:thin]',
          sticky && 'sticky',
        )}
        style={sticky ? { top: sticky } : undefined}
      >
        <TabList aria-label={label} className="flex min-w-max gap-1">
          {items.map((t) => (
            <Tab
              key={t.id}
              id={t.id}
              className={cx(
                'relative inline-flex h-11 cursor-pointer items-center gap-1.5 px-3 text-[13px] font-semibold text-fg-muted outline-hidden',
                'hover:text-fg data-[selected]:text-fg',
                'after:absolute after:inset-x-2 after:bottom-0 after:h-[2px] after:rounded-full after:bg-transparent data-[selected]:after:bg-accent',
                'data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus data-[focus-visible]:-outline-offset-2',
              )}
            >
              {t.label}
              {t.badge}
            </Tab>
          ))}
        </TabList>
      </div>
      {items.map((t) => (
        <TabPanel key={t.id} id={t.id} className="pt-5 outline-hidden">
          {t.content}
        </TabPanel>
      ))}
    </AriaTabs>
  );
}

/* ───────── Filters ───────── */

export function SelectField({
  label,
  value,
  onChange,
  options,
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  className?: string;
}) {
  return (
    <label className={cx('flex min-w-0 flex-col gap-1 text-[12px] text-fg-muted', className)}>
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 rounded-sm border border-line bg-surface-1 px-2.5 text-[13px] text-fg outline-hidden focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Removable chips for the active filters + «پاک کردن فیلترها». Wraps instead of clipping. */
export function ActiveFilters({
  items,
  onRemove,
  onClear,
}: {
  items: { id: string; label: string }[];
  onRemove: (id: string) => void;
  onClear: () => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-active-filters>
      {items.map((i) => (
        <span
          key={i.id}
          className="inline-flex items-center gap-1 rounded-full border border-line bg-surface-2 py-1 pe-1 ps-3 text-[12px] text-fg"
        >
          {i.label}
          <AriaButton
            aria-label={`حذف فیلتر ${i.label}`}
            onPress={() => onRemove(i.id)}
            className="grid size-6 cursor-pointer place-items-center rounded-full text-fg-muted outline-hidden hover:bg-surface-3 hover:text-fg data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus"
          >
            <X aria-hidden className="size-3.5" />
          </AriaButton>
        </span>
      ))}
      <AriaButton
        onPress={onClear}
        className="cursor-pointer rounded-sm px-2 py-1 text-[12px] font-semibold text-fg-muted underline-offset-4 outline-hidden hover:text-fg hover:underline data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus"
      >
        پاک کردن فیلترها
      </AriaButton>
    </div>
  );
}

/* ───────── Amount (display only) ───────── */

/** Tabular, LTR-isolated amount with a secondary unit. `null` → «—» (never zero). */
export function Amount({
  value,
  unit,
  hidden,
  className,
  unitClassName,
}: {
  value: string | null | undefined;
  unit: string;
  hidden?: boolean;
  className?: string;
  unitClassName?: string;
}) {
  if (value === null || value === undefined)
    return (
      <span className={cx('text-fg-subtle', className)} aria-label="نامشخص">
        —
      </span>
    );
  const text = formatAmount(value, unit, { withUnit: false });
  return (
    <span className={cx('inline-flex max-w-full flex-wrap items-baseline gap-x-1.5', className)}>
      <bdi dir="ltr" className="hx-num min-w-0 [overflow-wrap:anywhere]">
        {hidden ? '••••••' : text}
      </bdi>
      <span className={cx('text-[0.8em] font-medium text-fg-muted', unitClassName)}>
        {unit === 'TOMAN' ? 'تومان' : unit}
      </span>
    </span>
  );
}
